import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  upsertInvoice,
  listInvoices,
  deleteInvoice,
  getInvoiceSummary,
} from "@/lib/invoices.functions";
import { ArrowLeft, Plus, Trash2, Download, FileText, Printer } from "lucide-react";
import { formatMoney } from "@/lib/money";


export const Route = createFileRoute("/admin_/invoices")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "HMRC Invoicing — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: InvoicesPage,
});

type LineItem = { description: string; quantity: number; unit_price: number };
type InvoiceRow = {
  id: string;
  invoice_number: string;
  client_name: string;
  client_email: string | null;
  client_address: string | null;
  issue_date: string;
  due_date: string | null;
  line_items: LineItem[];
  subtotal: number;
  vat_rate: number;
  vat_amount: number;
  total: number;
  currency: string;
  status: string;
  notes: string | null;
};

const STATUSES = ["draft", "sent", "paid", "overdue", "void"] as const;

const fmt = (n: number, ccy = "USD") => formatMoney(n, ccy);


function nextInvoiceNumber(existing: InvoiceRow[]) {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;
  const nums = existing
    .map((i) => i.invoice_number)
    .filter((n) => n.startsWith(prefix))
    .map((n) => parseInt(n.slice(prefix.length), 10))
    .filter((n) => !isNaN(n));
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(4, "0")}`;
}

function InvoicesPage() {
  const qc = useQueryClient();
  const list = useServerFn(listInvoices);
  const summary = useServerFn(getInvoiceSummary);
  const upsert = useServerFn(upsertInvoice);
  const del = useServerFn(deleteInvoice);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editing, setEditing] = useState<InvoiceRow | null>(null);
  const [creating, setCreating] = useState(false);

  const invoicesQ = useQuery({
    queryKey: ["invoices", statusFilter, from, to],
    queryFn: () => list({ data: { status: statusFilter, from, to } }),
  });
  const summaryQ = useQuery({
    queryKey: ["invoices-summary", from, to],
    queryFn: () => summary({ data: { from, to } }),
  });

  const invoices = (invoicesQ.data?.invoices ?? []) as unknown as InvoiceRow[];

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Invoice deleted");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices-summary"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exportCSV = () => {
    const headers = [
      "invoice_number","client_name","client_email","issue_date","due_date",
      "subtotal","vat_rate","vat_amount","total","currency","status",
    ];
    const rows = invoices.map((i) =>
      [
        i.invoice_number, i.client_name, i.client_email ?? "", i.issue_date, i.due_date ?? "",
        i.subtotal, i.vat_rate, i.vat_amount, i.total, i.currency, i.status,
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hmrc-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Admin
            </Link>
            <h1 className="font-display text-2xl text-foreground sm:text-3xl">
              HMRC Invoicing
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 rounded-lg border border-gold/30 bg-card px-3 py-2 text-sm font-medium hover:bg-gold/10"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => {
                setEditing(null);
                setCreating(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--gold-deep)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> New Invoice
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Invoices", val: summaryQ.data?.count ?? 0, money: false },
            { label: "Subtotal", val: summaryQ.data?.subtotal ?? 0, money: true },
            { label: "VAT", val: summaryQ.data?.vat ?? 0, money: true },
            { label: "Total", val: summaryQ.data?.total ?? 0, money: true },
            { label: "Paid", val: summaryQ.data?.paid ?? 0, money: true },
            { label: "Outstanding", val: summaryQ.data?.outstanding ?? 0, money: true },
          ].map((c) => (
            <div
              key={c.label}
              className="rounded-xl border border-gold/15 bg-card p-3"
            >
              <div className="text-xs text-muted-foreground">{c.label}</div>
              <div className="mt-1 font-display text-lg text-foreground">
                {c.money ? fmt(Number(c.val)) : c.val}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-gold/15 bg-card p-3">
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-gold/20 bg-background px-2 py-1.5 text-sm"
            >
              <option value="all">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-gold/20 bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-gold/20 bg-background px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gold/15 bg-card">
          <table className="w-full text-sm">
            <thead className="bg-gold/5 text-start text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Issued</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2 text-end">Subtotal</th>
                <th className="px-3 py-2 text-end">VAT</th>
                <th className="px-3 py-2 text-end">Total</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {invoicesQ.isLoading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No invoices yet.</td></tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-gold/10">
                    <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-3 py-2">{inv.client_name}</td>
                    <td className="px-3 py-2">{inv.issue_date}</td>
                    <td className="px-3 py-2">{inv.due_date ?? "—"}</td>
                    <td className="px-3 py-2 text-end">{fmt(Number(inv.subtotal), inv.currency)}</td>
                    <td className="px-3 py-2 text-end">{fmt(Number(inv.vat_amount), inv.currency)}</td>
                    <td className="px-3 py-2 text-end font-semibold">{fmt(Number(inv.total), inv.currency)}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${
                        inv.status === "paid" ? "bg-green-100 text-green-800" :
                        inv.status === "overdue" ? "bg-red-100 text-red-800" :
                        inv.status === "sent" ? "bg-blue-100 text-blue-800" :
                        inv.status === "void" ? "bg-muted text-foreground" :
                        "bg-amber-100 text-amber-800"
                      }`}>{inv.status}</span>
                    </td>
                    <td className="px-3 py-2 text-end">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => { setEditing(inv); setCreating(true); }}
                          className="rounded-md border border-gold/20 px-2 py-1 text-xs hover:bg-gold/10"
                        >Edit</button>
                        <button
                          onClick={() => printInvoice(inv)}
                          className="rounded-md border border-gold/20 px-2 py-1 text-xs hover:bg-gold/10"
                          title="Print / Save as PDF"
                        ><Printer className="h-3.5 w-3.5" /></button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete ${inv.invoice_number}?`)) deleteMut.mutate(inv.id);
                          }}
                          className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                        ><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <InvoiceEditor
          initial={editing}
          existing={invoices}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSave={async (payload) => {
            try {
              await upsert({ data: payload });
              toast.success(payload.id ? "Invoice updated" : "Invoice created");
              qc.invalidateQueries({ queryKey: ["invoices"] });
              qc.invalidateQueries({ queryKey: ["invoices-summary"] });
              setCreating(false);
              setEditing(null);
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}

function InvoiceEditor({
  initial,
  existing,
  onClose,
  onSave,
}: {
  initial: InvoiceRow | null;
  existing: InvoiceRow[];
  onClose: () => void;
  onSave: (data: any) => void;
}) {
  const [form, setForm] = useState(() => ({
    id: initial?.id,
    invoice_number: initial?.invoice_number ?? nextInvoiceNumber(existing),
    client_name: initial?.client_name ?? "",
    client_email: initial?.client_email ?? "",
    client_address: initial?.client_address ?? "",
    issue_date: initial?.issue_date ?? new Date().toISOString().slice(0, 10),
    due_date: initial?.due_date ?? "",
    line_items: (initial?.line_items as LineItem[]) ?? [
      { description: "", quantity: 1, unit_price: 0 },
    ],
    vat_rate: initial?.vat_rate ?? 20,
    currency: initial?.currency ?? "USD",
    notes: initial?.notes ?? "",
    status: initial?.status ?? "draft",
  }));

  const totals = useMemo(() => {
    const subtotal = form.line_items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const vat = +(subtotal * (Number(form.vat_rate) / 100)).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), vat, total: +(subtotal + vat).toFixed(2) };
  }, [form.line_items, form.vat_rate]);

  const setLine = (idx: number, patch: Partial<LineItem>) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-card p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl">{form.id ? "Edit Invoice" : "New Invoice"}</h2>
          <button onClick={onClose} className="rounded-md px-3 py-1 text-sm hover:bg-muted">Close</button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Invoice Number">
            <input className={ip} value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className={ip} value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Client Name">
            <input className={ip} value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </Field>
          <Field label="Client Email">
            <input className={ip} value={form.client_email ?? ""}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
          </Field>
          <Field label="Client Address" className="sm:col-span-2">
            <textarea className={ip} rows={2} value={form.client_address ?? ""}
              onChange={(e) => setForm({ ...form, client_address: e.target.value })} />
          </Field>
          <Field label="Issue Date">
            <input type="date" className={ip} value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
          </Field>
          <Field label="Due Date">
            <input type="date" className={ip} value={form.due_date ?? ""}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>
          <Field label="VAT Rate (%)">
            <input type="number" step="0.01" className={ip} value={form.vat_rate}
              onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })} />
          </Field>
          <Field label="Currency">
            <select className={ip} value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </Field>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Line Items</h3>
            <button
              onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }] })}
              className="inline-flex items-center gap-1 rounded-md border border-gold/20 px-2 py-1 text-xs hover:bg-gold/10"
            ><Plus className="h-3 w-3" /> Add</button>
          </div>
          <div className="space-y-2">
            {form.line_items.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2">
                <input className={`${ip} col-span-6`} placeholder="Description"
                  value={li.description} onChange={(e) => setLine(idx, { description: e.target.value })} />
                <input type="number" step="0.01" className={`${ip} col-span-2`} placeholder="Qty"
                  value={li.quantity} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) })} />
                <input type="number" step="0.01" className={`${ip} col-span-3`} placeholder="Unit price"
                  value={li.unit_price} onChange={(e) => setLine(idx, { unit_price: Number(e.target.value) })} />
                <button
                  onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })}
                  disabled={form.line_items.length === 1}
                  className="col-span-1 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
                ><Trash2 className="mx-auto h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        <Field label="Notes" className="mt-4">
          <textarea rows={2} className={ip} value={form.notes ?? ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>

        <div className="mt-4 rounded-lg bg-gold/5 p-3 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmt(totals.subtotal, form.currency)}</span></div>
          <div className="flex justify-between"><span>VAT ({form.vat_rate}%)</span><span>{fmt(totals.vat, form.currency)}</span></div>
          <div className="mt-1 flex justify-between border-t border-gold/20 pt-1 font-semibold">
            <span>Total</span><span>{fmt(totals.total, form.currency)}</span>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gold/20 px-4 py-2 text-sm hover:bg-gold/10">Cancel</button>
          <button
            onClick={() => onSave(form)}
            className="rounded-md bg-[var(--gold-deep)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >Save</button>
        </div>
      </div>
    </div>
  );
}

const ip = "w-full rounded-md border border-gold/20 bg-background px-3 py-1.5 text-sm";

function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block text-xs ${className}`}>
      <span className="mb-1 block text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function printInvoice(inv: InvoiceRow) {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  const rows = inv.line_items
    .map(
      (li) =>
        `<tr><td>${escapeHtml(li.description)}</td><td style="text-align:right">${li.quantity}</td><td style="text-align:right">${fmt(Number(li.unit_price), inv.currency)}</td><td style="text-align:right">${fmt(Number(li.quantity) * Number(li.unit_price), inv.currency)}</td></tr>`,
    )
    .join("");
  w.document.write(`<!doctype html><html><head><title>${inv.invoice_number}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:40px;color:#222;max-width:800px;margin:auto}
  h1{margin:0 0 6px;font-size:28px}
  .muted{color:#666;font-size:13px}
  table{width:100%;border-collapse:collapse;margin-top:20px}
  th,td{padding:8px 10px;border-bottom:1px solid #eee;font-size:14px;text-align:left}
  th{background:#fafafa;font-size:12px;text-transform:uppercase;color:#555}
  .totals{margin-top:16px;margin-left:auto;width:280px}
  .totals div{display:flex;justify-content:space-between;padding:4px 0}
  .totals .grand{border-top:2px solid #222;font-weight:700;margin-top:6px;padding-top:6px}
  .grid{display:flex;justify-content:space-between;gap:30px;margin-top:18px}
  .badge{display:inline-block;padding:2px 8px;border-radius:999px;background:#eee;font-size:12px;text-transform:uppercase}
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start">
  <div><h1>INVOICE</h1><div class="muted">${inv.invoice_number}</div></div>
  <div style="text-align:right"><span class="badge">${inv.status}</span><div class="muted" style="margin-top:6px">Issued: ${inv.issue_date}</div>${inv.due_date ? `<div class="muted">Due: ${inv.due_date}</div>` : ""}</div>
</div>
<div class="grid">
  <div><div class="muted">Bill to</div><strong>${escapeHtml(inv.client_name)}</strong>${inv.client_email ? `<div>${escapeHtml(inv.client_email)}</div>` : ""}${inv.client_address ? `<div style="white-space:pre-line">${escapeHtml(inv.client_address)}</div>` : ""}</div>
</div>
<table><thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
<div class="totals">
  <div><span>Subtotal</span><span>${fmt(Number(inv.subtotal), inv.currency)}</span></div>
  <div><span>VAT (${inv.vat_rate}%)</span><span>${fmt(Number(inv.vat_amount), inv.currency)}</span></div>
  <div class="grand"><span>Total</span><span>${fmt(Number(inv.total), inv.currency)}</span></div>
</div>
${inv.notes ? `<div style="margin-top:24px"><div class="muted">Notes</div>${escapeHtml(inv.notes)}</div>` : ""}
<div class="muted" style="margin-top:30px;font-size:11px">Generated for HMRC record-keeping.</div>
<script>window.onload=()=>window.print()</script>
</body></html>`);
  w.document.close();
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
